import { createReducer, on } from '@ngrx/store';
import { ContentGroupFilter } from '../domain/content-group-filter';
import { Feed } from '../domain/feed';
import { Program } from '../domain/program';
import * as Actions from './content-group-edit-screen.actions';

export const featureKey = 'cxbContentGroupEditScreen';

export interface FeatureState {
  loading: boolean;
  programs: Program[];
  searchedPrograms: Program[];
  contentGroup: Feed;
  originContentGroupFilter: ContentGroupFilter;
  originContentGroupPinnedContentIds: number[];
  originIsDynamic: boolean;
  loadingPreview: boolean;
  loadingSearch: boolean;
}

export interface AppState {
  [featureKey]: FeatureState;
}

export const initialState: FeatureState = {
  loading: false,
  programs: undefined,
  searchedPrograms: undefined,
  contentGroup: undefined,
  originContentGroupFilter: undefined,
  originContentGroupPinnedContentIds: undefined,
  originIsDynamic: undefined,
  loadingPreview: false,
  loadingSearch: false,
};

export const reducer = createReducer(
  initialState,

  on(Actions.fetchContentGroup, (state): FeatureState => ({ ...state, loading: true })),
  on(
    Actions.fetchContentGroupSuccess,
    (state, action): FeatureState => ({
      ...state,
      contentGroup: action.contentGroup,
      loading: false,
    })
  ),
  on(Actions.fetchContentGroupFail, (state): FeatureState => ({ ...state, loading: false })),

  on(Actions.loadCxbContentGroupPreview, (state): FeatureState => ({ ...state, loadingPreview: true })),
  on(
    Actions.loadCxbContentGroupPreviewSuccess,
    (state, action): FeatureState => ({
      ...state,
      programs: action.programs,
      loadingPreview: false,
    })
  ),
  on(Actions.loadCxbContentGroupPreviewFail, (state): FeatureState => ({ ...state, loadingPreview: false })),

  on(Actions.searchCxbPrograms, (state): FeatureState => ({ ...state, loadingSearch: true })),
  on(
    Actions.searchCxbProgramsSuccess,
    (state, action): FeatureState => ({
      ...state,
      searchedPrograms: action.programs,
      loadingSearch: false,
    })
  ),
  on(Actions.searchCxbProgramsFail, (state): FeatureState => ({ ...state, loadingSearch: false })),

  on(
    Actions.setCxbPinnedContentIds,
    (state, action): FeatureState => ({
      ...state,
      contentGroup: {
        ...state.contentGroup,
        pinnedContentIds: [...action.contentIds],
      },
    })
  ),
  on(
    Actions.addCxbPinnedContentId,
    (state, action): FeatureState => ({
      ...state,
      contentGroup: {
        ...state.contentGroup,
        pinnedContentIds: addContactIdToContentGroupPinnedContentIds(state.contentGroup.pinnedContentIds, action.contentId),
      },
    })
  ),
  on(
    Actions.deleteCxbPinnedContentId,
    (state, action): FeatureState => ({
      ...state,
      contentGroup: {
        ...state.contentGroup,
        pinnedContentIds: deleteContentIdFromContentGroupPinnedContentIds(state.contentGroup.pinnedContentIds, action.contentId),
      },
    })
  ),

  on(
    Actions.setCxbContentGroupFilter,
    (state, action): FeatureState => ({
      ...state,
      contentGroup: {
        ...state.contentGroup,
        filter: action.filter,
      },
    })
  ),

  on(Actions.setCxbContentGroup, (state, action): FeatureState => ({ ...state, contentGroup: action.contentGroup })),

  on(Actions.setCxbOriginContentGroupFilter, (state, action): FeatureState => setOriginContentGroupFilter(state, action.filter)),
  on(
    Actions.setCxbOriginContentGroupPinnedContentIds,
    (state, action): FeatureState => setOriginContentGroupPinnedContentIds(state, action.pinnedContentIds)
  ),

  on(
    Actions.setCxbContentGroupIsDynamic,
    (state, action): FeatureState => ({
      ...state,
      contentGroup: { ...state.contentGroup, isDynamic: action.isDynamic },
      originIsDynamic: state.originIsDynamic === undefined ? action.isDynamic : state.originIsDynamic,
    })
  ),

  on(Actions.updateCxbContentGroupMeta, (state): FeatureState => ({ ...state, loading: true })),
  on(
    Actions.updateCxbContentGroupMetaSuccess,
    (state, action): FeatureState => ({
      ...state,
      contentGroup: {
        ...state.contentGroup,
        title: action.contentGroup.title,
        description: action.contentGroup.description,
      },
      loading: false,
    })
  ),
  on(Actions.updateCxbContentGroupMetaFail, (state): FeatureState => ({ ...state, loading: false })),

  on(Actions.updateCxbContentGroup, (state): FeatureState => ({ ...state, loading: true })),
  on(
    Actions.updateCxbContentGroupSuccess,
    (state): FeatureState => ({
      ...state,
      loading: false,
      contentGroup: undefined,
      originContentGroupFilter: undefined,
      originContentGroupPinnedContentIds: undefined,
      originIsDynamic: undefined,
    })
  ),
  on(Actions.updateCxbContentGroupFail, (state): FeatureState => ({ ...state, loading: false })),

  on(Actions.deleteCxbContentGroup, (state): FeatureState => ({ ...state, loading: true })),
  on(
    Actions.deleteCxbContentGroupSuccess,
    // eslint-disable-next-line sonarjs/no-identical-functions
    (state): FeatureState => ({
      ...state,
      loading: false,
      contentGroup: undefined,
      originContentGroupFilter: undefined,
      originContentGroupPinnedContentIds: undefined,
      originIsDynamic: undefined,
    })
  ),
  on(Actions.deleteCxbContentGroupFail, (state): FeatureState => ({ ...state, loading: false })),
  on(Actions.resetCxbContentGroupScreenState, (): FeatureState => ({ ...initialState }))
);

const addContactIdToContentGroupPinnedContentIds = (pinnedContentIds: number[], contentId: number): number[] => {
  return Array.from(new Set([contentId, ...pinnedContentIds]));
};

const deleteContentIdFromContentGroupPinnedContentIds = (pinnedContentIds: number[], contentId: number): number[] => {
  const contentIds = [...pinnedContentIds];
  const index = contentIds.findIndex((id) => id === contentId);
  if (index <= -1) {
    return contentIds;
  }
  contentIds.splice(index, 1);
  return [...contentIds];
};

const setOriginContentGroupFilter = (state: FeatureState, filter: ContentGroupFilter) => {
  if (state.originContentGroupFilter) {
    return { ...state };
  }
  return { ...state, originContentGroupFilter: filter };
};

const setOriginContentGroupPinnedContentIds = (state: FeatureState, pinnedContentIds: number[]) => {
  if (state.originContentGroupPinnedContentIds) {
    return { ...state };
  }
  return { ...state, originContentGroupPinnedContentIds: pinnedContentIds };
};
